import React, { useState, useEffect } from 'react';
import { Modal } from '../../../Components/Modal';
import { Button } from '../../../Components/Button';
import { Input } from '../../../Components/Input';
import { Title, Text } from '../../../Components/Typography';
import { PrecioCopia } from '../../../hooks/usePreciosCopias';
import './BulkEditModal.scss';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPrecios: PrecioCopia[];
  onSave: (updates: { id: number; newPrice: number }[]) => Promise<void>;
  loading: boolean;
}

type EditMode = 'percentage' | 'fixed' | 'custom';

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  selectedPrecios,
  onSave,
  loading
}) => {
  const [editMode, setEditMode] = useState<EditMode>('percentage');
  const [percentageValue, setPercentageValue] = useState<string>('');
  const [fixedValue, setFixedValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [previewUpdates, setPreviewUpdates] = useState<{ 
    id: number; 
    currentPrice: number; 
    newPrice: number;
    color: string;
    papel: string;
    papelWeight: number;
    hoja: string;
    hojaWidth: number;
    hojaHeight: number;
    faz: string;
    isProblematic?: boolean;
  }[]>([]);

  useEffect(() => {
    if (isOpen) {
      // Limpiar completamente todos los estados al abrir el modal
      setPercentageValue('');
      setFixedValue('');
      setError('');
      setPreviewUpdates([]);
    }
  }, [isOpen]);

  const calculateNewPrices = () => {
    if (!selectedPrecios.length) return [];

    const updates = selectedPrecios.map(precio => {
      let newPrice = precio.precio;

      switch (editMode) {
        case 'percentage':
          const percentage = parseFloat(percentageValue);
          if (!isNaN(percentage)) {
            newPrice = precio.precio * (1 + percentage / 100);
          }
          break;
        case 'fixed':
          const fixed = parseFloat(fixedValue);
          if (!isNaN(fixed)) {
            newPrice = precio.precio + fixed;
          }
          break;
        case 'custom':
          // Para modo personalizado, cada precio se edita individualmente
          newPrice = precio.precio;
          break;
      }

      const finalPrice = Math.round(newPrice * 100) / 100;
      const isProblematic = finalPrice <= 0;

      return {
        id: precio.id,
        currentPrice: precio.precio,
        newPrice: finalPrice,
        isProblematic,
        // Información descriptiva del precio
        color: precio.color_name,
        papel: precio.papel_name,
        papelWeight: precio.papel_weight,
        hoja: precio.hoja_name,
        hojaWidth: precio.hoja_width,
        hojaHeight: precio.hoja_height,
        faz: precio.faz_name
      };
    });

    setPreviewUpdates(updates);
    return updates;
  };

  useEffect(() => {
    if (isOpen) {
      if (percentageValue || fixedValue) {
        calculateNewPrices();
      } else {
        // Si no hay valores, limpiar la vista previa
        setPreviewUpdates([]);
      }
    }
  }, [editMode, percentageValue, fixedValue, selectedPrecios, isOpen]);

  const handleSave = async () => {
    if (selectedPrecios.length === 0) return;

    const updates = calculateNewPrices();
    
    if (updates.length === 0) {
      setError('No hay cambios para aplicar');
      return;
    }

    // Filtrar solo los precios que no son problemáticos (se excluyen automáticamente)
    const validUpdates = updates.filter(update => !update.isProblematic);

    if (validUpdates.length === 0) {
      setError('No hay precios válidos para aplicar');
      return;
    }

    try {
      await onSave(validUpdates.map(update => ({ id: update.id, newPrice: update.newPrice })));
      // Limpiar todos los estados antes de cerrar
      setPercentageValue('');
      setFixedValue('');
      setError('');
      setPreviewUpdates([]);
      onClose();
    } catch (err) {
      setError('Error al guardar los cambios');
    }
  };

  const handleClose = () => {
    setPercentageValue('');
    setFixedValue('');
    setError('');
    setPreviewUpdates([]);
    onClose();
  };

  const getValidUpdatesCount = () => {
    return previewUpdates.filter(update => !update.isProblematic).length;
  };

  const getProblematicUpdates = () => {
    return previewUpdates.filter(update => update.isProblematic);
  };

  const formatPrice = (price: number) => {
    return `$${price.toLocaleString('es-AR')}`;
  };

  const formatDimensions = (width: number, height: number) => {
    return `${width} × ${height} mm`;
  };

  const getPriceDescription = (update: any) => {
    return `${update.papel} ${update.papelWeight}g - ${update.hoja} (${formatDimensions(update.hojaWidth, update.hojaHeight)}) - ${update.color} - ${update.faz}`;
  };

  const getTotalChange = () => {
    if (previewUpdates.length === 0) return 0;
    return previewUpdates.reduce((total, update) => total + (update.newPrice - update.currentPrice), 0);
  };

  const getAverageChange = () => {
    if (previewUpdates.length === 0) return 0;
    return getTotalChange() / previewUpdates.length;
  };

  return (
         <Modal
       title={`Edición Masiva - ${selectedPrecios.length} precios seleccionados`}
       open={isOpen}
       onClose={handleClose}
     >
      <div className="bulk-edit-modal">
        {/* Modo de edición */}
        <div className="edit-mode-section">
          <Text bold>Modo de Edición:</Text>
          <div className="mode-buttons">
            <Button
              onClick={() => {
                setEditMode('percentage');
                setFixedValue(''); // Limpiar campo de monto fijo
                setError(''); // Limpiar errores
                setPreviewUpdates([]); // Limpiar vista previa
              }}
              outlined={editMode !== 'percentage'}
              small
              style={editMode === 'percentage' ? { backgroundColor: '#FE7C40', color: 'white' } : {}}
            >
              📊 Porcentaje
            </Button>
            <Button
              onClick={() => {
                setEditMode('fixed');
                setPercentageValue(''); // Limpiar campo de porcentaje
                setError(''); // Limpiar errores
                setPreviewUpdates([]); // Limpiar vista previa
              }}
              outlined={editMode !== 'fixed'}
              small
              style={editMode === 'fixed' ? { backgroundColor: '#FE7C40', color: 'white' } : {}}
            >
              💰 Monto Fijo
            </Button>
          </div>
        </div>

        {/* Input según el modo */}
        <div className="input-section">
          {editMode === 'percentage' && (
            <div className="input-group">
              <Input
                title="Porcentaje de cambio"
                placeholder="Ej: 10 para aumentar 10%, -5 para disminuir 5%"
                type="number"
                value={percentageValue}
                onChange={setPercentageValue}
                error={error}
              />
              <Text className="input-help">
                Ingrese un número positivo para aumentar, negativo para disminuir
              </Text>
            </div>
          )}

          {editMode === 'fixed' && (
            <div className="input-group">
              <Input
                title="Monto fijo a agregar/quitar"
                placeholder="Ej: 5 para agregar $5, -2 para quitar $2"
                type="number"
                value={fixedValue}
                onChange={setFixedValue}
                error={error}
              />
              <Text className="input-help">
                Ingrese un número positivo para agregar, negativo para quitar
              </Text>
            </div>
          )}
        </div>

        {/* Vista previa de cambios */}
        {previewUpdates.length > 0 && (
          <div className="preview-section">
            <div className="preview-header">
              <Text bold>Vista Previa de Cambios</Text>
              <div className="preview-stats">
                <div className="stat">
                  <Text>Total: {formatPrice(getTotalChange())}</Text>
                </div>
                <div className="stat">
                  <Text>Promedio: {formatPrice(getAverageChange())}</Text>
                </div>
                <div className="stat">
                  <Text>Válidos: {getValidUpdatesCount()}</Text>
                </div>
              </div>
            </div>

            {/* Mostrar precios problemáticos primero */}
            {getProblematicUpdates().length > 0 && (
              <div className="problematic-section">
                <div className="problematic-header">
                  <div className="warning-text">
                    ⚠️ {getProblematicUpdates().length} productos serán excluidos automáticamente. Sus precios se convertirían en valores negativos o cero
                  </div>
                </div>
                <div className="problematic-list">
                  {getProblematicUpdates().map((update) => (
                    <div key={update.id} className="preview-item problematic">
                      <div className="item-info">
                        <Text className="price-description">{getPriceDescription(update)}</Text>
                        <Text className="price-change problematic">
                          {formatPrice(update.currentPrice)} → {formatPrice(update.newPrice)}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mostrar precios válidos */}
            <div className="valid-prices-section">
              <Text bold>Precios Válidos</Text>
              <div className="preview-list">
                {previewUpdates
                  .filter(update => !update.isProblematic)
                  .slice(0, 10)
                  .map((update, index) => (
                    <div key={update.id} className="preview-item">
                      <div className="item-info">
                        <Text className="price-description">{getPriceDescription(update)}</Text>
                        <Text className="price-change">
                          {formatPrice(update.currentPrice)} → {formatPrice(update.newPrice)}
                        </Text>
                      </div>
                      <div className="change-indicator">
                        {update.newPrice > update.currentPrice ? (
                          <span className="increase">+{formatPrice(update.newPrice - update.currentPrice)}</span>
                        ) : (
                          <span className="decrease">{formatPrice(update.newPrice - update.currentPrice)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                
                {previewUpdates.filter(update => !update.isProblematic).length > 10 && (
                  <div className="more-items">
                    <Text>... y {previewUpdates.filter(update => !update.isProblematic).length - 10} precios más</Text>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Acciones */}
        <div className="modal-actions">
          <Button 
            onClick={handleClose}
            outlined
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={loading || getValidUpdatesCount() === 0}
            loading={loading}
          >
            {`Aplicar Cambios (${getValidUpdatesCount()} precios)`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
